import { inject, Injectable } from '@angular/core';
import { WA_LOCATION, WA_WINDOW } from '@ng-web-apis/common';
import { environment } from '@environments/environment';

// New-app hostname → classic-app dashboard. Hosts not listed here get no link.
const CLASSIC_APP_URLS: Record<string, string> = {
  'next.prod.inscyd.com': 'https://app.inscyd.com/home/dashboard',
  'next.test.inscyd.com': 'https://app.test.inscyd.com/home/dashboard',
  'next.dev.inscyd.com': 'https://app.dev.inscyd.com/home/dashboard',
};

// Each app keeps its own copy of the choice (localStorage is per-origin). The switch links carry
// `?frontend=` so the receiving app can overwrite its stale copy instead of bouncing the user back.
const STORAGE_KEY = 'frontend';
const PARAM = 'frontend';
type Frontend = 'classic' | 'new';

/** Resolves where "Switch to classic INSCYD" should send the user, and remembers that choice. */
@Injectable({ providedIn: 'root' })
export class ClassicAppService {
  private readonly location = inject(WA_LOCATION);
  private readonly window = inject(WA_WINDOW);
  private readonly target = this.resolveTarget();

  readonly url: string | null = this.target ? this.withParam(this.target, 'classic') : null;

  /**
   * Runs before bootstrap. An explicit `?frontend=` wins and is stored; otherwise a stored
   * 'classic' choice redirects. Resolves never while redirecting so the new app doesn't flash.
   */
  init(): Promise<void> | void {
    if (!this.target) return;

    const url = new URL(this.location.href);
    const requested = url.searchParams.get(PARAM);

    if (requested === 'classic' || requested === 'new') {
      localStorage.setItem(STORAGE_KEY, requested);
      url.searchParams.delete(PARAM);
      this.window.history.replaceState(this.window.history.state, '', url);
      return;
    }

    if (localStorage.getItem(STORAGE_KEY) === 'classic') {
      this.location.replace(this.withParam(this.target, 'classic'));
      return new Promise(() => {});
    }
  }

  switchToClassic(): void {
    localStorage.setItem(STORAGE_KEY, 'classic');
  }

  private resolveTarget(): string | null {
    const target = CLASSIC_APP_URLS[this.location.hostname];
    // Guard against a target that is this very app — redirecting would loop.
    return target && new URL(target).origin !== this.location.origin ? target : null;
  }

  private withParam(target: string, frontend: Frontend): string {
    const url = new URL(target);
    url.searchParams.set(PARAM, frontend);
    return url.toString();
  }
}
